import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Search, MapPin, SlidersHorizontal, X, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { CATEGORIES, CITIES } from '../mock/mock';
import LawFirmCard from '../components/LawFirmCard';
import SEO from '../components/SEO';
import { firmsApi } from '../api/client';

function FiltersPanel({ q, setQ, city, setCity, category, setCategory, minRating, setMinRating, verifiedOnly, setVerifiedOnly, clearAll }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="text-xs font-semibold text-[#153e44] uppercase tracking-wider mb-2 block">Buscar</label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, área..." className="pl-9" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#153e44] uppercase tracking-wider mb-2 block">Cidade</label>
        <Select value={city || 'all'} onValueChange={(v) => setCity(v === 'all' ? '' : v)}>
          <SelectTrigger><SelectValue placeholder="Todas as cidades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as cidades</SelectItem>
            {CITIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#153e44] uppercase tracking-wider mb-2 block">Área do direito</label>
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          <button onClick={() => setCategory('')} className={`w-full text-left px-3 py-2 rounded-[7px] text-sm transition-colors ${!category ? 'bg-[#153e44] text-white font-medium' : 'hover:bg-[#f2f6f7] text-[#354c54]'}`}>Todas as áreas</button>
          {CATEGORIES.map(c => (
            <button key={c.slug} onClick={() => setCategory(c.slug)} className={`w-full text-left px-3 py-2 rounded-[7px] text-sm transition-colors ${category === c.slug ? 'bg-[#153e44] text-white font-medium' : 'hover:bg-[#f2f6f7] text-[#354c54]'}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#153e44] uppercase tracking-wider mb-3 block">Avaliação mínima: {minRating.toFixed(1)}</label>
        <Slider value={[minRating]} min={0} max={5} step={0.5} onValueChange={(v) => setMinRating(v[0])} />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="verified" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} />
        <label htmlFor="verified" className="text-sm text-[#354c54] cursor-pointer">Apenas verificados OAB</label>
      </div>
      <Button variant="outline" onClick={clearAll} className="w-full">Limpar filtros</Button>
    </div>
  );
}

export default function Listing() {
  const { citySlug, catSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get('q') || '');
  const [city, setCity] = useState(citySlug || searchParams.get('city') || '');
  const [category, setCategory] = useState(catSlug || searchParams.get('cat') || '');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('featured');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [items, setItems] = useState([]);
  const [sponsoredItems, setSponsoredItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCity(citySlug || searchParams.get('city') || '');
    setCategory(catSlug || searchParams.get('cat') || '');
    setQ(searchParams.get('q') || '');
  }, [citySlug, catSlug, searchParams]);

  useEffect(() => {
    setLoading(true);
    const params = { sort: sortBy };
    if (city) params.city = city;
    if (category) params.category = category;
    if (q) params.q = q;
    if (verifiedOnly) params.verified = true;
    if (minRating > 0) params.minRating = minRating;
    firmsApi.list(params)
      .then(d => { setItems(d.items); setTotal(d.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
    firmsApi.list({ ...params, sponsored: true, limit: 3 })
      .then(d => setSponsoredItems(d.items))
      .catch(() => setSponsoredItems([]));
  }, [city, category, q, verifiedOnly, minRating, sortBy]);

  const cityData = useMemo(() => CITIES.find(c => c.slug === city), [city]);
  const catData = useMemo(() => CATEGORIES.find(c => c.slug === category), [category]);

  const pageTitle = catData && cityData
    ? `Advogados de ${catData.name} em ${cityData.name}`
    : catData ? `Advogados de ${catData.name}`
    : cityData ? `Advogados em ${cityData.name}/${cityData.state}`
    : 'Todos os escritórios de advocacia';

  const seoDesc = cityData
    ? `Encontre os melhores advogados${catData ? ` especializados em ${catData.name}` : ''} em ${cityData.name}/${cityData.state}. ${total} escritórios verificados pela OAB.`
    : catData ? `Escritórios especializados em ${catData.name} em todo o Brasil. Compare avaliações reais.`
    : 'Lista completa de escritórios de advocacia no Brasil.';

  const clearAll = () => {
    setQ(''); setCity(''); setCategory(''); setVerifiedOnly(false); setMinRating(0);
    setSearchParams({});
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: pageTitle,
    numberOfItems: total,
    itemListElement: items.slice(0, 10).map((f, i) => ({
      '@type': 'ListItem', position: i + 1,
      url: `${typeof window !== 'undefined' ? window.location.origin : ''}/escritorio/${f.slug}`,
      name: f.name,
    })),
  };

  return (
    <div>
      <SEO title={pageTitle} description={seoDesc} jsonLd={jsonLd} />

      <div className="bg-white border-b border-[#cbd8dc]">
        <div className="aj-wrap py-6">
          <nav className="flex items-center gap-1 text-xs text-[#52676e] mb-3">
            <Link to="/" className="hover:text-[#153e44]">Início</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/advogados" className="hover:text-[#153e44]">Advogados</Link>
            {cityData && (<><ChevronRight className="w-3 h-3" /><span>{cityData.name}</span></>)}
            {catData && (<><ChevronRight className="w-3 h-3" /><span>{catData.name}</span></>)}
          </nav>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#153e44]">{pageTitle}</h1>
          <p className="text-[#425a62] mt-1 text-sm">{total} escritório{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}{cityData && ` em ${cityData.name}`}</p>
          {cityData && catData && (
            <Link to={`/advogado/${catData.slug}/em/${cityData.slug}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-[#153e44] bg-[#edf4f5] hover:bg-[#0c3036]/20 border border-[#cbd8dc] rounded-full px-3 py-1 transition-colors">
              Ver página especializada: Advogado {catData.name} em {cityData.name} <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      <div className="aj-wrap py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white border border-[#cbd8dc] rounded-[10px] p-5">
              <h3 className="font-semibold text-[#153e44] mb-4 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filtros</h3>
              <FiltersPanel q={q} setQ={setQ} city={city} setCity={setCity} category={category} setCategory={setCategory} minRating={minRating} setMinRating={setMinRating} verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly} clearAll={clearAll} />
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between gap-3 mb-5">
              <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setShowMobileFilters(true)}><SlidersHorizontal className="w-4 h-4 mr-1.5" /> Filtros</Button>
              <div className="flex-1 lg:flex-none lg:ml-auto">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full lg:w-56 h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Destaques primeiro</SelectItem>
                    <SelectItem value="rating">Melhor avaliados</SelectItem>
                    <SelectItem value="reviews">Mais avaliações</SelectItem>
                    <SelectItem value="experience">Mais experientes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!loading && sponsoredItems.length > 0 && (
              <div className="mb-6 bg-[#f2f6f7]/70 border border-[#cbd8dc] rounded-[10px] p-4" data-testid="sponsored-module">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest bg-[#153e44] text-white px-2 py-0.5 rounded">Patrocinado</span>
                  <span className="text-xs text-[#52676e]">Destaque pago identificado — não altera a ordenação orgânica abaixo</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {sponsoredItems.map(f => <LawFirmCard key={`sp-${f.id}`} firm={f} />)}
                </div>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-[10px] border border-[#cbd8dc] h-96 animate-pulse" />)}
              </div>
            ) : items.length === 0 && cityData && !q && !category && !verifiedOnly && !minRating ? (
              <div className="aj-edition bg-[#f7fafb] rounded-r-[10px] p-6 md:p-8" data-testid="city-empty-invite">
                <span className="aj-kicker">Ainda não há escritórios em {cityData.name}/{cityData.state}</span>
                <h3 className="text-[23px] md:text-[27px] leading-[1.3] mt-2 mb-2">Seja o primeiro escritório de {cityData.name} no Ambito Advogados.</h3>
                <p className="text-[#354c54] max-w-[590px] mb-5">Crie um perfil gratuito, apareça para quem busca advogado em {cityData.name} e receba contatos direto no WhatsApp. Sem venda de leads, sem comissão.</p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <Link to={`/cadastro?city=${cityData.slug}`} className="aj-button aj-primary" data-testid="city-empty-register-btn">Cadastrar meu escritório <span aria-hidden="true">→</span></Link>
                  <Link to="/cidades" className="aj-link py-3 inline-block">Ver outras cidades <span aria-hidden="true">→</span></Link>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-12 text-center">
                <MapPin className="w-10 h-10 text-[#6b858e] mx-auto mb-3" />
                <h3 className="font-semibold text-[#153e44] text-lg">Nenhum escritório encontrado</h3>
                <p className="text-[#425a62] text-sm mt-1">Tente ajustar os filtros.</p>
                <Button onClick={clearAll} className="mt-4 bg-[#153e44] hover:bg-[#0c3036]">Limpar filtros</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {items.map(f => <LawFirmCard key={f.id} firm={f} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileFilters(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-[#153e44] text-lg">Filtros</h3>
              <button onClick={() => setShowMobileFilters(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <FiltersPanel q={q} setQ={setQ} city={city} setCity={setCity} category={category} setCategory={setCategory} minRating={minRating} setMinRating={setMinRating} verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly} clearAll={clearAll} />
            <Button className="w-full mt-6 bg-[#153e44] hover:bg-[#0c3036]" onClick={() => setShowMobileFilters(false)}>Ver {total} resultados</Button>
          </div>
        </div>
      )}
    </div>
  );
}
